import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import { getDbForUser, type PlannerDoc } from "@/lib/db";
import { PAIN_ENTRY_TYPE, type PainEntryData, painEntryId } from "@/lib/pain";

function docToEntry(doc: PlannerDoc): PainEntryData {
  const data = (doc.data ?? {}) as Partial<PainEntryData>;
  return {
    date: typeof data.date === "string" ? data.date : "",
    level: typeof data.level === "number" ? data.level : undefined,
    symptoms: Array.isArray(data.symptoms) ? data.symptoms : [],
    note: typeof data.note === "string" ? data.note : undefined,
  };
}

export function usePainEntry(date: string | null): {
  entry: PainEntryData | null;
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    entry: PainEntryData | null;
    loading: boolean;
  }>({ entry: null, loading: true });

  useEffect(() => {
    if (!user || !date) {
      setState({ entry: null, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .findOne(painEntryId(user.id, date))
        .$.subscribe((doc) => {
          setState({
            entry: doc ? docToEntry(doc.toJSON() as PlannerDoc) : null,
            loading: false,
          });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, date]);

  return state;
}

/**
 * All entries with startDate ≤ date ≤ endDate, sorted ascending by date.
 * Queries by the indexed (user_id, type) pair and filters the date range in
 * memory — the per-user collection is small.
 */
export function usePainEntries(
  startDate: string | null,
  endDate: string | null,
): { entries: PainEntryData[]; loading: boolean } {
  const { user } = useSession();
  const [state, setState] = useState<{
    entries: PainEntryData[];
    loading: boolean;
  }>({ entries: [], loading: true });

  useEffect(() => {
    if (!user || !startDate || !endDate) {
      setState({ entries: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({ selector: { user_id: user.id, type: PAIN_ENTRY_TYPE } })
        .$.subscribe((docs) => {
          const entries = docs
            .map((d) => docToEntry(d.toJSON() as PlannerDoc))
            .filter((e) => e.date >= startDate && e.date <= endDate)
            .sort((a, b) => (a.date < b.date ? -1 : 1));
          setState({ entries, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, startDate, endDate]);

  return state;
}
